import {
  INDICATOR_COLORS,
  INDICATOR_LABELS,
  INDICATOR_NAMES,
  MAX_DEVIATIONS,
  MAX_PERIOD,
  MIN_DEVIATIONS,
  MIN_PERIOD,
  clamp,
  describe,
  newIndicator,
  type Indicator,
  type IndicatorKind,
} from '@/lib/indicators'

/**
 * Panneau des indicateurs : ajouter, régler, retirer.
 *
 * Replié par défaut, comme les outils de tracé : sur un téléphone, trois
 * indicateurs dépliés en permanence prendraient la place du graphique.
 *
 * Chaque indicateur se règle SUR PLACE, dans sa propre ligne. Une fenêtre de
 * réglage séparée obligerait à fermer, regarder, rouvrir — alors qu'on choisit
 * une période en voyant la courbe bouger.
 */
export function IndicatorTools({
  open,
  onToggle,
  indicators,
  onChange,
  available,
}: {
  open: boolean
  onToggle: () => void
  indicators: Indicator[]
  onChange: (indicators: Indicator[]) => void
  /**
   * Bougies actuellement chargées.
   *
   * Sert à dire pourquoi une courbe manque. Une moyenne à 200 sur un
   * graphique qui ne porte que 180 bougies ne peut pas exister : sans un mot,
   * on croit l'indicateur cassé et on recommence. Le nombre de bougies dépend
   * de l'intervalle — voir CANDLE_COUNT — donc passer en M1 peut suffire à
   * faire réapparaître la courbe.
   */
  available: number
}) {
  function update(id: string, patch: Partial<Indicator>) {
    onChange(indicators.map((item) => (item.id === id ? { ...item, ...patch } : item)))
  }

  function add(kind: IndicatorKind) {
    onChange([...indicators, newIndicator(kind, indicators.length)])
  }

  return (
    <>
      <button
        type="button"
        onClick={onToggle}
        aria-pressed={open}
        className={`min-h-touch rounded-md border px-3 text-[12px] transition-colors ${
          open || indicators.length > 0
            ? 'border-accent bg-accent/15 text-accent'
            : 'border-border bg-elevated text-ink-muted hover:bg-border/60'
        }`}
      >
        ∿ Indicateurs
        {indicators.length > 0 && (
          <span className="num ml-1 text-[10px] opacity-70">{indicators.length}</span>
        )}
      </button>

      {open && (
        <div className="flex w-full flex-col gap-2 rounded-md border border-border bg-elevated/60 p-1.5">
          {indicators.length === 0 && (
            <p className="text-[11px] leading-snug text-ink-muted">
              Aucun indicateur. Ils se calculent dans le navigateur, sur les bougies
              affichées, et s'appliquent à toutes les paires.
            </p>
          )}

          {indicators.map((indicator) => (
            /*
              Deux rangs fixes, et non une seule ligne qui se replie.
              --------------------------------------------------------------
              Repliée sur 375 px, chaque partie prenait son propre rang :
              nom, période, écarts, couleurs, suppression — cinq rangs pour
              un indicateur, et le panneau remplissait l'écran dès le
              deuxième. Le nom et la suppression tiennent sur le premier rang,
              tous les réglages sur le second.
            */
            <div
              key={indicator.id}
              className="flex flex-col gap-1.5 rounded border border-border bg-panel p-1.5"
            >
              <div className="flex items-center justify-between gap-2">
                {/* Rappel de ce qu'on règle : avec trois indicateurs ouverts,
                    les champs se ressemblent tous. */}
                <span
                  className="num flex min-w-0 items-center gap-1.5 truncate text-[12px] font-semibold"
                  style={{ color: indicator.color }}
                  title={INDICATOR_NAMES[indicator.kind]}
                >
                  <span
                    aria-hidden
                    className="h-2.5 w-2.5 shrink-0 rounded-full"
                    style={{ backgroundColor: indicator.color }}
                  />
                  {describe(indicator)}
                </span>

                <button
                  type="button"
                  onClick={() =>
                    onChange(indicators.filter((item) => item.id !== indicator.id))
                  }
                  aria-label={`Retirer ${describe(indicator)}`}
                  className="min-h-touch shrink-0 rounded border border-down/50 px-2.5 text-[11px] font-semibold text-down active:bg-down/10"
                >
                  ✕
                </button>
              </div>

              {/* Il faut deux points pour tracer un trait : une période
                  égale au nombre de bougies n'en donnerait qu'un, invisible. */}
              {indicator.period >= available && (
                <p className="text-[10px] leading-snug text-warn">
                  Période trop longue : <span className="num">{available}</span> bougies
                  chargées sur cet intervalle. Raccourcissez la période, ou passez sur un
                  intervalle plus court.
                </p>
              )}

              <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5">
                <label className="flex items-center gap-1">
                  <span className="text-[10px] uppercase tracking-wide text-ink-muted">
                    période
                  </span>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={String(indicator.period)}
                    onChange={(event) => {
                      const parsed = Number(event.target.value.replace(/\D/g, ''))
                      // On accepte la frappe intermédiaire — effacer pour
                      // retaper passe par une valeur vide — mais on ne range
                      // jamais hors bornes : une période de 0 boucle.
                      if (!event.target.value) return update(indicator.id, { period: MIN_PERIOD })
                      if (!Number.isFinite(parsed)) return
                      update(indicator.id, { period: clamp(parsed, MIN_PERIOD, MAX_PERIOD) })
                    }}
                    className="num h-touch w-14 rounded border border-border bg-elevated px-1.5 text-center text-ink outline-none focus:border-accent"
                  />
                </label>

                {/* Les écarts-types n'existent que pour Bollinger : les
                    afficher ailleurs ferait chercher ce qu'ils règlent. */}
                {indicator.kind === 'bollinger' && (
                  <label className="flex items-center gap-1">
                    <span className="text-[10px] uppercase tracking-wide text-ink-muted">
                      écarts
                    </span>
                    <input
                      type="text"
                      inputMode="decimal"
                      value={String(indicator.deviations)}
                      onChange={(event) => {
                        const parsed = Number(event.target.value.replace(',', '.'))
                        if (!event.target.value) {
                          return update(indicator.id, { deviations: MIN_DEVIATIONS })
                        }
                        if (!Number.isFinite(parsed)) return
                        update(indicator.id, {
                          deviations: clamp(parsed, MIN_DEVIATIONS, MAX_DEVIATIONS),
                        })
                      }}
                      className="num h-touch w-14 rounded border border-border bg-elevated px-1.5 text-center text-ink outline-none focus:border-accent"
                    />
                  </label>
                )}

                <div className="ml-auto flex gap-1">
                  {INDICATOR_COLORS.map((teinte) => (
                    <button
                      key={teinte}
                      type="button"
                      aria-label={`Couleur ${teinte}`}
                      onClick={() => update(indicator.id, { color: teinte })}
                      style={{ backgroundColor: teinte }}
                      className={`h-7 w-7 rounded-full transition-transform ${
                        indicator.color === teinte ? 'scale-110 ring-2 ring-white' : 'opacity-70'
                      }`}
                    />
                  ))}
                </div>
              </div>
            </div>
          ))}

          {/* Ajout : un bouton par type, plutôt qu'un menu déroulant suivi
              d'un « ajouter ». Trois types tiennent sur une ligne. */}
          <div className="flex flex-wrap gap-1.5">
            {(['sma', 'ema', 'bollinger'] as const).map((kind) => (
              <button
                key={kind}
                type="button"
                onClick={() => add(kind)}
                title={INDICATOR_NAMES[kind]}
                className="min-h-touch flex-1 rounded border border-border bg-panel px-2 text-[12px] font-semibold text-ink hover:bg-border/60 active:bg-border"
              >
                + {INDICATOR_LABELS[kind]}
              </button>
            ))}
          </div>

          {indicators.length > 0 && (
            <button
              type="button"
              onClick={() => onChange([])}
              className="min-h-touch rounded border border-down/50 px-2 text-[11px] text-down active:bg-down/10"
            >
              Tout retirer
            </button>
          )}
        </div>
      )}
    </>
  )
}
